/**
 * The platform layer — the ONE place the app may know what it is running in.
 *
 * MyDeck ships from one codebase to three targets (docs/MOBILE-PLAN.md): the
 * desktop web app, the iPhone home-screen app (a PWA) and the Android app
 * (Capacitor). A page never asks which one it is in. It calls a capability
 * here, and here decides whether that becomes a native call, a web
 * equivalent, or nothing.
 *
 * 1. **Capacitor may be imported in this directory and nowhere else.**
 *    `eslint.config.js` enforces it: a page importing a plugin directly breaks
 *    the web build, which has no such module.
 * 2. **Every capability does nothing off-native — it never throws.** The
 *    desktop web app keeps running exactly the code it ran before.
 *
 * Detection reads the `Capacitor` global that the Android shell injects,
 * rather than importing `@capacitor/core`, so this module compiles and is
 * correct before Capacitor is installed (mobile phase M2).
 */

/** The slice of the Capacitor runtime global this module reads. */
type CapacitorGlobal = {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
};

function capacitor(): CapacitorGlobal | undefined {
  if (typeof window === "undefined") return undefined;
  return (window as Window & { Capacitor?: CapacitorGlobal }).Capacitor;
}

/** True inside the Android app. False in every browser, installed or not. */
export function isNative(): boolean {
  return capacitor()?.isNativePlatform?.() === true;
}

/** True in the Android app specifically — kept apart from `isNative()` so a
 *  future second native target cannot inherit Android-only behaviour. */
export function isAndroidApp(): boolean {
  return isNative() && capacitor()?.getPlatform?.() === "android";
}

/** True when opened from the home screen as an installed web app, rather than
 *  in a browser tab. `navigator.standalone` is the only signal iPhone Safari
 *  gives; `display-mode` covers everything else. */
export function isInstalledPWA(): boolean {
  if (typeof window === "undefined") return false;
  const standalone =
    window.matchMedia?.("(display-mode: standalone)").matches === true ||
    window.matchMedia?.("(display-mode: fullscreen)").matches === true;
  const iosStandalone =
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return standalone || iosStandalone;
}

/** True on an iPhone or iPad — the target that gets the home-screen app.
 *  iPadOS reports itself as a Mac, so the touch-point check is what catches
 *  an iPad at all. */
export function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
}

/** True in iPhone **Safari** specifically: Chrome and Firefox on an iPhone
 *  can't add to the home screen, so the hint is for Safari alone. */
export function isIOSSafari(): boolean {
  if (!isIOS()) return false;
  return !/CriOS|FxiOS|EdgiOS|OPiOS|GSA\//.test(navigator.userAgent);
}

/** What this client is, in one word. */
export type ClientType = "android" | "pwa" | "web";

export function clientType(): ClientType {
  if (isNative()) return "android";
  return isInstalledPWA() ? "pwa" : "web";
}

/**
 * Where the server is, prepended to every API path.
 *
 * In a browser the app and the API share one origin (FastAPI serves the SPA in
 * production; Vite proxies `/api` in dev), so this is `""` and every request
 * is exactly what it was before the mobile work.
 *
 * Inside the Android app, pages load from the files on the phone
 * (`https://localhost`), so a relative `/api` would ask the phone. The Android
 * build stamps the real server in at build time (`MYDECK_NATIVE_API_ORIGIN`,
 * `vite.config.ts`, `--mode android` only); the web build carries `""` and
 * never reads it.
 */
export function apiBase(): string {
  return isNative() ? __MYDECK_NATIVE_API_ORIGIN__ : "";
}

/* ------------------------------------------------------------ the hardware back button
 *
 * What back *does* is app policy, in `lib/back.ts`; this only connects the
 * button. `@capacitor/app` is imported on native alone, so a browser never
 * downloads or runs it.
 */

/** Take over Android's back button. Registering replaces Capacitor's default
 *  (history back, else close) entirely. A no-op off-native. */
export function onBackButton(handler: (canGoBack: boolean) => void): void {
  if (!isNative()) return;
  void import("@capacitor/app").then(({ App }) =>
    App.addListener("backButton", ({ canGoBack }) => handler(canGoBack)),
  );
}

/** Send the app to the background, the way Android leaves any app from its
 *  first screen. Nothing is lost: since Android 12 this only backgrounds it. */
export function leaveApp(): void {
  if (!isNative()) return;
  void import("@capacitor/app").then(({ App }) => App.minimizeApp());
}

/** What this phone calls itself ("Pixel 8", "moto g54 5G") — the label
 *  Settings → Devices shows. Null in a browser, and null if the plugin fails:
 *  the server then labels it "Android app". */
export async function deviceName(): Promise<string | null> {
  if (!isNative()) return null;
  try {
    const { Device } = await import("@capacitor/device");
    const info = await Device.getInfo();
    const name = (info.name || "").trim();
    const model = (info.model || "").trim();
    // `name` is the owner's own label for the phone when Android shares it;
    // otherwise the maker and model.
    if (name && name !== model) return name;
    const maker = (info.manufacturer || "").trim();
    return [maker, model].filter(Boolean).join(" ") || null;
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------ haptics
 *
 * A short buzz on the Android app only (plan M3: refresh done, item marked,
 * card switched). Through the WebView's Vibration API — the manifest declares
 * VIBRATE — so no plugin is needed. A browser stays silent: a desktop has no
 * motor, and a phone browser buzzing at a web page is unwelcome.
 */
export type Haptic = "tick" | "success" | "warning";

const PATTERNS: Record<Haptic, number | number[]> = {
  tick: 8,
  success: [10, 40, 18],
  warning: [24, 60, 24],
};

export function haptic(kind: Haptic = "tick"): void {
  if (!isNative()) return;
  if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
  try {
    navigator.vibrate?.(PATTERNS[kind]);
  } catch {
    /* no motor, or vibration disabled — silence is the right fallback */
  }
}

/* ------------------------------------------------------------- the biometric check (M4)
 *
 * Fingerprint or face, with the phone's own PIN/pattern as the fallback, via
 * @capgo/capacitor-native-biometric (Compass's choice). Android only: a
 * browser and the iPhone home-screen app have no equivalent.
 */

/** Whether this phone can prove its owner is holding it — an enrolled
 *  fingerprint or face, or at least a screen lock. False off-native. */
export async function biometricAvailable(): Promise<boolean> {
  if (!isNative()) return false;
  try {
    const { NativeBiometric } = await import("@capgo/capacitor-native-biometric");
    const result = await NativeBiometric.isAvailable({ useFallback: true });
    return result.isAvailable || result.deviceIsSecure === true;
  } catch {
    return false;
  }
}

/** Show the system's check. True only for a verified owner; a cancel, a
 *  failure or no support is false. */
export async function verifyOwner(reason: string): Promise<boolean> {
  if (!isNative()) return false;
  try {
    const { NativeBiometric } = await import("@capgo/capacitor-native-biometric");
    await NativeBiometric.verifyIdentity({ reason, title: "Unlock MyDeck", useFallback: true, maxAttempts: 5 });
    return true;
  } catch {
    return false;
  }
}
